import React from 'react';
import { Dimensions, ScrollView, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { toHtmlTextStyle } from '../components/general/toHtmlTextStyle';

const WIDTH = Dimensions.get('window').width;
const PAGE_HORIZONTAL_PADDING = WIDTH >= 1200 ? 100 : 24;

const safetyPrinciples = [
    {
        icon: 'office-building-outline',
        title: 'Verified business legitimacy',
        description:
            'SnapCamp is designed to work with camps that operate as legitimate businesses. We emphasize clear business identity, accountable operators, and a professional presence families can trust.',
        accent: '#2563EB',
    },
    {
        icon: 'file-certificate-outline',
        title: 'Official licenses and documentation',
        description:
            'We focus on camps that maintain required licenses, permits, or operating documentation where applicable, helping families feel more confident that listed programs meet real-world operating standards.',
        accent: '#0EA5E9',
    },
    {
        icon: 'school-outline',
        title: 'Qualified coaches and staff',
        description:
            'Coach and staff quality matters. SnapCamp highlights programs that present coaching credentials, qualifications, and experience clearly so families can better evaluate the people leading each program.',
        accent: '#059669',
    },
    {
        icon: 'chart-box-outline',
        title: 'Transparent enrollment information',
        description:
            'Families should be able to see meaningful enrollment details, session availability, and program information without guesswork. Transparency helps parents compare options with more confidence.',
        accent: '#7C3AED',
    },
];

const trustSignals = [
    {
        title: 'Camp profiles with accountable information',
        description:
            'Listings should present the camp name, program focus, schedule details, location, and operational information in a way that helps families understand who is running the camp.',
    },
    {
        title: 'Coach and camp ratings',
        description:
            'Ratings and feedback create additional visibility into program quality, coach reputation, and family experience over time.',
    },
    {
        title: 'Clear enrollment visibility',
        description:
            'Availability, sessions, and enrollment-related information should be easy to review so parents can make informed decisions before registering.',
    },
    {
        title: 'Better comparison across options',
        description:
            'When camps present credentials, business details, and performance signals clearly, families can compare programs more fairly and confidently.',
    },
];

const CampSafetyScreen = () => {
    return (
        <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
            <View style={styles.container}>
                <View style={styles.hero}>
                    <View style={styles.heroBadge}>
                        <MaterialCommunityIcons name="shield-check-outline" size={16} color="#1D4ED8" />
                        <Text style={styles.heroBadgeText}>Camp Safety</Text>
                    </View>

                    <h1 style={toHtmlTextStyle(styles.heroTitle, { marginTop: 0 })}>Trust, transparency, and higher standards for camps listed on SnapCamp.</h1>
                    <Text style={styles.heroSubtitle}>
                        SnapCamp is built to help families evaluate camps more confidently by emphasizing legitimate business operations, official documentation, qualified coaches, and transparent enrollment and ratings data.
                    </Text>
                </View>

                <View style={styles.sectionCard}>
                    <Text style={styles.sectionTitle}>What camp safety means on SnapCamp</Text>
                    <Text style={styles.sectionSubtitle}>
                        We focus on the trust signals that matter most to families when choosing a camp and to camps that want to present themselves professionally.
                    </Text>

                    <View style={styles.principlesGrid}>
                        {safetyPrinciples.map((item) => (
                            <View key={item.title} style={styles.principleCard}>
                                <View style={[styles.principleIconWrap, { backgroundColor: `${item.accent}18` }]}>
                                    <MaterialCommunityIcons name={item.icon as any} size={24} color={item.accent} />
                                </View>
                                <Text style={styles.principleTitle}>{item.title}</Text>
                                <Text style={styles.principleDescription}>{item.description}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                <View style={styles.sectionCard}>
                    <Text style={styles.sectionTitle}>How transparency helps families choose better</Text>
                    <Text style={styles.sectionSubtitle}>
                        The more clearly a camp presents its qualifications, operating standards, and performance signals, the easier it is for families to make informed decisions.
                    </Text>

                    <View style={styles.signalList}>
                        {trustSignals.map((item) => (
                            <View key={item.title} style={styles.signalRow}>
                                <View style={styles.signalBullet}>
                                    <MaterialCommunityIcons name="check" size={16} color="#2563EB" />
                                </View>
                                <View style={styles.signalCopy}>
                                    <Text style={styles.signalTitle}>{item.title}</Text>
                                    <Text style={styles.signalDescription}>{item.description}</Text>
                                </View>
                            </View>
                        ))}
                    </View>
                </View>

                <View style={styles.noteCard}>
                    <View style={styles.noteIconWrap}>
                        <MaterialCommunityIcons name="information-outline" size={22} color="#0F172A" />
                    </View>
                    <View style={styles.noteCopy}>
                        <Text style={styles.noteTitle}>Why this matters</Text>
                        <Text style={styles.noteText}>
                            Families should not have to rely on vague descriptions or incomplete information. SnapCamp’s safety approach is centered on giving parents clearer signals about legitimacy, qualifications, and camp quality before enrollment.
                        </Text>
                    </View>
                </View>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#F8FAFC',
    },
    pageContent: {
        paddingTop: 36,
        paddingBottom: 72,
        paddingHorizontal: PAGE_HORIZONTAL_PADDING,
    },
    container: {
        width: '100%',
        gap: 28,
    },
    hero: {
        backgroundColor: '#FFFFFF',
        borderRadius: 28,
        padding: 32,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#0F172A',
        shadowOpacity: 0.06,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 8 },
    },
    heroBadge: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        backgroundColor: '#DBEAFE',
        marginBottom: 18,
    },
    heroBadgeText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#1D4ED8',
    },
    heroTitle: {
        fontSize: 36,
        lineHeight: 44,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 14,
    },
    heroSubtitle: {
        fontSize: 17,
        lineHeight: 28,
        color: '#475569',
    },
    sectionCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 28,
        padding: 32,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#0F172A',
        shadowOpacity: 0.05,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 6 },
    },
    sectionTitle: {
        fontSize: 30,
        lineHeight: 38,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 10,
    },
    sectionSubtitle: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
        marginBottom: 22,
    },
    principlesGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        gap: 16,
    },
    principleCard: {
        flexBasis: 0,
        flexGrow: 1,
        minWidth: 260,
        backgroundColor: '#F8FAFC',
        borderRadius: 22,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        padding: 22,
    },
    principleIconWrap: {
        width: 52,
        height: 52,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    principleTitle: {
        fontSize: 20,
        lineHeight: 26,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 10,
    },
    principleDescription: {
        fontSize: 15,
        lineHeight: 24,
        color: '#475569',
    },
    signalList: {
        gap: 18,
    },
    signalRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 14,
        paddingBottom: 18,
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
    },
    signalBullet: {
        width: 28,
        height: 28,
        borderRadius: 999,
        backgroundColor: '#DBEAFE',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 2,
    },
    signalCopy: {
        flex: 1,
    },
    signalTitle: {
        fontSize: 18,
        lineHeight: 24,
        fontWeight: '700',
        color: '#0F172A',
        marginBottom: 4,
    },
    signalDescription: {
        fontSize: 15,
        lineHeight: 24,
        color: '#475569',
    },
    noteCard: {
        backgroundColor: '#E2E8F0',
        borderRadius: 24,
        padding: 24,
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 16,
    },
    noteIconWrap: {
        width: 46,
        height: 46,
        borderRadius: 14,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
    },
    noteCopy: {
        flex: 1,
    },
    noteTitle: {
        fontSize: 18,
        lineHeight: 24,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 6,
    },
    noteText: {
        fontSize: 15,
        lineHeight: 24,
        color: '#334155',
    },
});

export default CampSafetyScreen;
